// FE-SCRM-048 — Step 4 (success): full-page success state with the
// commit counts and a "View Renewal Board" CTA. The wizard renders
// a Toast underneath the page header so the producer sees the
// "23 prospects imported" confirmation as they navigate back.

import { useNavigate } from "react-router-dom";

import { Button } from "@/design-system";

import type { ImportSession } from "./types";


export interface SuccessStepProps {
  session: ImportSession;
  on_restart: () => void;
}


export function SuccessStep({ session, on_restart }: SuccessStepProps) {
  const navigate = useNavigate();
  const summary = session.commit_summary ?? {};
  const created = session.import_type === "prospects"
    ? (summary.prospects_created ?? 0)
    : (summary.contacts_created ?? 0);
  const subject = session.import_type === "prospects"
    ? (created === 1 ? "prospect" : "prospects")
    : (created === 1 ? "contact" : "contacts");

  return (
    <div
      data-testid="import-step-success"
      className="flex flex-col items-center gap-4 rounded-lg bg-recessed px-6 py-16 text-center"
    >
      <div
        className={[
          "flex h-12 w-12 items-center justify-center rounded-full",
          "bg-sage text-text-inverse",
        ].join(" ")}
        aria-hidden
      >
        <span className="text-2xl">✓</span>
      </div>
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-text-primary">
          {created} {subject} imported
        </h2>
        {session.import_type === "prospects"
          && (summary.form5500_matches_queued ?? 0) > 0 && (
          <p className="text-xs text-text-secondary">
            {summary.form5500_matches_queued} queued for Form 5500
            intelligence matching.
          </p>
        )}
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={on_restart}
        >
          Import another file
        </Button>
        {session.import_type === "prospects" ? (
          <Button
            type="button"
            variant="primary"
            onClick={() => navigate("/dashboard/sales-crm?tab=pipeline")}
          >
            View Renewal Board
          </Button>
        ) : (
          <Button
            type="button"
            variant="primary"
            onClick={() => navigate("/dashboard/sales-crm?tab=prospects")}
          >
            View Prospects
          </Button>
        )}
      </div>
    </div>
  );
}
